import { PacAction, PacBeatContext, PacWidget, resolveWidgetSay } from '@bariendo/lib/patient-access-script';
import { PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import {
  Anchor,
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { IconAlertTriangle, IconChevronRight, IconExternalLink } from '@tabler/icons-react';
import { ReactNode, useContext, useState } from 'react';
import { PacColumnsLayout, PacLayoutContext, QSA_GRID, useAnswerWidth } from './pacLayout';
import { useMarketOptions } from '@/hooks/useMarketOptions';
import { ORBIT_NEEDS_ACTION } from '@/theme/colors';

interface WidgetProps {
  widget: PacWidget;
  answers: PatientAccessCallAnswers;
  /** What a group's `when` reads besides the answers (BAR-2919). */
  context?: PacBeatContext;
  setValue: (id: string, value: string | string[] | boolean | undefined) => void;
  onAction: (action: PacAction) => void;
  pendingAction?: PacAction;
  /**
   * Actions this viewer cannot take. Their links show disabled rather than
   * failing on click, e.g. Create payment order for a viewer who cannot take
   * payments (BAR-3070).
   */
  unavailableActions?: readonly PacAction[];
  disabled?: boolean;
  /**
   * Rendering for the widget kinds only one script has — its computed fields and
   * its grid. Kept as an escape hatch rather than props per kind so that adding a
   * script does not widen this component: the warm hand-off card's procedure-date
   * grid arrives here as just another kind this returns for.
   */
  renderCardWidget?: (widget: PacWidget) => ReactNode;
}

/** Whether the Script column is drawn. */
function hasScriptColumn(layout: PacColumnsLayout): boolean {
  return layout.scripted && layout.showScript;
}

const HAIRLINE = '1px solid var(--mantine-color-gray-2)';

/**
 * One Question · Script · Answer row. Without the Script column the answer
 * takes its place as well. A row with nothing left to show once the script is
 * hidden draws nothing.
 */
export function QsaRow({
  question,
  script,
  children,
}: {
  question?: ReactNode;
  script?: string;
  children?: ReactNode;
}) {
  const layout = useContext(PacLayoutContext) ?? { showScript: true, scripted: true };
  const withScript = hasScriptColumn(layout);
  if (!question && !children && !(withScript && script)) return null;
  return (
    <Box py={8} style={{ ...QSA_GRID, borderTop: HAIRLINE, alignItems: 'start' }}>
      <Text size="sm" pt={6} style={{ minWidth: 0 }}>
        {question}
      </Text>
      {withScript && (
        <Text size="sm" c="dimmed" pt={6} style={{ minWidth: 0, whiteSpace: 'pre-line' }}>
          {script
            ?.split('\n\n')
            .map((line) => `“${line}”`)
            .join('\n\n')}
        </Text>
      )}
      <Box
        style={{
          gridColumn: withScript ? undefined : '2 / span 2',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: 6,
          minHeight: 32,
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/**
 * A line the rep says, in the patient-facing register.
 *
 * Rendered verbatim: tokens like [procedure] and [today's verified price] stay
 * literal so the team reads the same sentence on every call.
 */
export function ScriptLine({ children }: { children: ReactNode }) {
  return (
    <Box pl="xs" my={4} style={{ borderLeft: '2px solid var(--mantine-color-gray-3)' }}>
      <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
        {children}
      </Text>
    </Box>
  );
}

export function QuotedScript({ text }: { text: string }) {
  const layout = useContext(PacLayoutContext);
  if (layout) return <QsaRow script={text} />;
  return <ScriptLine>&ldquo;{text}&rdquo;</ScriptLine>;
}

/**
 * A question or action title. Bold so it stands apart from the gray script under it.
 * `say` is the line read for it: under the row on the shipped card, in the
 * Script column on v2.0.
 */
export function Row({ label, say, children }: { label: ReactNode; say?: string; children: ReactNode }) {
  const layout = useContext(PacLayoutContext);
  if (layout) {
    return (
      <QsaRow question={label} script={say}>
        {children}
      </QsaRow>
    );
  }
  const row = (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm" py={4}>
      <Text size="sm" fw={600} style={{ flex: 1, minWidth: 0 }}>
        {label}
      </Text>
      {children}
    </Group>
  );
  if (!say) return row;
  return (
    <Box>
      {row}
      <QuotedScript text={say} />
    </Box>
  );
}

/** Chip row used for both single- and multi-select. `labels` names an option where its stored value reads differently. */
export function Chips({
  options,
  selected,
  onToggle,
  disabled,
  labels,
}: {
  options: readonly string[];
  selected: string[];
  onToggle: (option: string) => void;
  disabled?: boolean;
  labels?: Readonly<Record<string, string>>;
}) {
  const columns = Boolean(useContext(PacLayoutContext));
  return (
    <Group gap={4} justify={columns ? 'flex-start' : 'flex-end'} style={columns ? undefined : { maxWidth: '62%' }}>
      {options.map((option) => {
        const isOn = selected.includes(option);
        return (
          <Button
            key={option}
            size="compact-xs"
            radius="xl"
            variant={isOn ? 'light' : 'default'}
            color={isOn ? 'green' : 'gray'}
            disabled={disabled}
            onClick={() => onToggle(option)}
          >
            {labels?.[option] ?? option}
          </Button>
        );
      })}
    </Group>
  );
}

/**
 * Compile-time exhaustiveness for the widget switch: a new `PacWidget` kind
 * fails to type-check until the switch renders it. At runtime — a stale lib
 * build, a cast — an unknown kind renders nothing rather than crashing the card.
 */
function renderUnhandled(_widget: never): null {
  return null;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asArray(value: unknown): string[] {
  return Array.isArray(value) ? (value as string[]) : [];
}

/**
 * Its own component because of the hook.
 *
 * A `select` may declare `optionsSource: 'markets'`, whose list is fetched from
 * Medplum — and a hook cannot be called from inside the `switch` below. Extracting
 * the arm keeps `useMarketOptions` unconditional. The hook sits on
 * `useSearchResources`, so several selects on one card share a cached request
 * rather than issuing one each.
 */
function SelectWidget({
  widget,
  answers,
  setValue,
  disabled,
  say,
}: {
  widget: Extract<PacWidget, { kind: 'select' }>;
  answers: PatientAccessCallAnswers;
  setValue: WidgetProps['setValue'];
  disabled?: boolean;
  say?: string;
}) {
  const { markets } = useMarketOptions();
  const options = widget.optionsSource === 'markets' ? markets : (widget.options ?? []);
  const current = asString(answers.values[widget.id]) || null;
  // A stored value the registry no longer offers still needs an entry, or Mantine
  // renders the Select empty and the rep silently loses what was recorded.
  const data = current && !options.includes(current) ? [...options, current] : [...options];
  const width = useAnswerWidth();

  return (
    <Row label={widget.label} say={say}>
      <Select
        size="xs"
        w={width}
        clearable
        disabled={disabled}
        data={data}
        value={current}
        onChange={(value) => setValue(widget.id, value ?? undefined)}
      />
    </Row>
  );
}

/**
 * Renders `**bold**` runs inside a reference line.
 *
 * The FAQ's facts are a centre list and a price list, and both are only legible
 * with the key on each line picked out. A whole markdown renderer for one
 * emphasis mark would be worse than this.
 */
function EmphasisedText({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*)/g).map((part, index) =>
        part.startsWith('**') && part.endsWith('**') ? (
          <Text key={index} component="span" size="xs" fw={600} c="dark">
            {part.slice(2, -2)}
          </Text>
        ) : (
          part
        )
      )}
    </>
  );
}

/**
 * One FAQ, closed until the rep taps it.
 *
 * Its own component because of the state: ten answers and their branches laid
 * out flat is a wall of script to read past while a caller waits, and a rep
 * looking for one of them needs titles, not paragraphs. Nothing is recorded —
 * `answers` is not touched here — so open/closed lives in the component and dies
 * with it.
 *
 * An open panel shows, in order: how callers phrase it (so the rep can confirm
 * they opened the right one), the standard answer and each branch, the reference
 * facts, and the never-say rule.
 */
function FaqWidget({ widget }: { widget: Extract<PacWidget, { kind: 'faq' }> }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box>
      <UnstyledButton onClick={() => setIsOpen((open) => !open)} w="100%" py={4} aria-expanded={isOpen}>
        <Group gap={6} wrap="nowrap" align="center">
          <IconChevronRight
            size={12}
            style={{ flexShrink: 0, transition: 'transform 150ms ease', transform: isOpen ? 'rotate(90deg)' : 'none' }}
          />
          <Text size="sm" fw={600} style={{ flex: 1, minWidth: 0 }}>
            {widget.question}
          </Text>
        </Group>
      </UnstyledButton>

      {isOpen && (
        <Box pl={18} pb={6}>
          {widget.asks && widget.asks.length > 0 && (
            <Text size="xs" c="dimmed" fs="italic" mb={4}>
              {widget.asks.map((ask) => `“${ask}”`).join('  ·  ')}
            </Text>
          )}

          {widget.responses.map((response, index) => (
            <ScriptLine key={index}>
              {response.when && (
                <Text component="span" size="xs" fw={600} c="dark">
                  {response.when}{' '}
                </Text>
              )}
              &ldquo;{response.say}&rdquo;
            </ScriptLine>
          ))}

          {widget.facts?.map((fact, index) => (
            <Text key={index} size="xs" c="dimmed" py={1} style={{ lineHeight: 1.45 }}>
              <EmphasisedText text={fact} />
            </Text>
          ))}

          {widget.never && (
            <Group gap={6} wrap="nowrap" align="flex-start" pt={4}>
              <IconAlertTriangle size={14} color={ORBIT_NEEDS_ACTION} style={{ flexShrink: 0, marginTop: 2 }} />
              <Text size="xs" c={ORBIT_NEEDS_ACTION}>
                {widget.never}
              </Text>
            </Group>
          )}
        </Box>
      )}
    </Box>
  );
}

export function PatientAccessCallWidget(props: WidgetProps) {
  const {
    widget,
    answers,
    context,
    setValue,
    onAction,
    pendingAction,
    unavailableActions,
    disabled,
    renderCardWidget,
  } = props;
  const columns = Boolean(useContext(PacLayoutContext));

  // The explanation beat names the procedure out loud, so which line a widget
  // speaks can depend on the procedure recorded on the note. `resolveWidgetSay`
  // owns that choice; every arm below just renders what it returns.
  const procedure = typeof answers.values.procedure === 'string' ? answers.values.procedure : undefined;
  const say = resolveWidgetSay(widget, procedure);

  switch (widget.kind) {
    case 'checkbox':
      return (
        <Box>
          <Checkbox
            size="xs"
            color="green"
            disabled={disabled}
            checked={answers.values[widget.id] === true}
            onChange={(event) => setValue(widget.id, event.currentTarget.checked)}
            label={<Text size="sm">{widget.label}</Text>}
          />
          {say && <QuotedScript text={say} />}
        </Box>
      );

    case 'select':
      return <SelectWidget widget={widget} answers={answers} setValue={setValue} disabled={disabled} say={say} />;

    case 'chips': {
      const current = asString(answers.values[widget.id]);
      return (
        <Row label={widget.label} say={say}>
          <Chips
            options={widget.options}
            selected={current ? [current] : []}
            disabled={disabled}
            // Tapping the selected chip clears it, so a misclick is recoverable.
            onToggle={(option) => setValue(widget.id, current === option ? undefined : option)}
          />
        </Row>
      );
    }

    case 'multiChips': {
      const current = asArray(answers.values[widget.id]);
      return (
        <Row label={widget.label} say={say}>
          <Chips
            options={widget.options}
            labels={widget.optionLabels}
            selected={current}
            disabled={disabled}
            onToggle={(option) =>
              setValue(
                widget.id,
                current.includes(option) ? current.filter((entry) => entry !== option) : [...current, option]
              )
            }
          />
        </Row>
      );
    }

    // A script action carries no control. The rep reads the label and the line
    // under it; nothing is captured, so nothing here touches `answers`.
    case 'action':
      if (columns) return <QsaRow question={widget.label} script={say} />;
      return (
        <Box py={4}>
          <Text size="sm" fw={600}>
            {widget.label}
          </Text>
          {say && <QuotedScript text={say} />}
        </Box>
      );

    case 'script':
      return <QuotedScript text={widget.text} />;

    case 'faq':
      return <FaqWidget widget={widget} />;

    // A coaching line for the rep — neutral, unlike the amber warning.
    case 'note':
      if (columns) {
        return (
          <QsaRow>
            <Text size="sm" c="dimmed">
              {widget.text}
            </Text>
          </QsaRow>
        );
      }
      return (
        <Text size="xs" c="dimmed" py={4}>
          {widget.text}
        </Text>
      );

    // The one typed field. Full width under its title — a textarea has no
    // business squeezed into the right half of a row.
    case 'text':
      if (columns) {
        return (
          <QsaRow question={widget.label} script={say}>
            <Textarea
              size="xs"
              w="100%"
              autosize
              minRows={2}
              disabled={disabled}
              aria-label={widget.label}
              value={asString(answers.values[widget.id])}
              onChange={(event) => setValue(widget.id, event.currentTarget.value || undefined)}
            />
          </QsaRow>
        );
      }
      return (
        <Box py={4}>
          <Text size="sm" fw={600} mb={4}>
            {widget.label}
          </Text>
          <Textarea
            size="xs"
            autosize
            minRows={2}
            disabled={disabled}
            aria-label={widget.label}
            value={asString(answers.values[widget.id])}
            onChange={(event) => setValue(widget.id, event.currentTarget.value || undefined)}
          />
        </Box>
      );

    case 'conditionalScript':
      if (columns) return <QsaRow question={widget.condition} script={widget.text} />;
      return (
        <ScriptLine>
          <Text component="span" size="xs" fw={600} c="dark">
            {widget.condition}{' '}
          </Text>
          &ldquo;{widget.text}&rdquo;
        </ScriptLine>
      );

    case 'warning':
      return (
        <Group gap={6} wrap="nowrap" py={4}>
          <IconAlertTriangle size={14} color={ORBIT_NEEDS_ACTION} style={{ flexShrink: 0 }} />
          <Text size="xs" c={ORBIT_NEEDS_ACTION}>
            {widget.text}
          </Text>
        </Group>
      );

    case 'actionLink': {
      const isPending = pendingAction === widget.action;
      const unavailable = unavailableActions?.includes(widget.action) ?? false;
      return (
        <Row label={widget.label} say={say}>
          <Anchor
            component="button"
            type="button"
            size="sm"
            c="green.9"
            disabled={disabled || isPending || unavailable}
            onClick={() => onAction(widget.action)}
          >
            <Group gap={4} wrap="nowrap">
              {widget.linkText}
              {isPending ? <Loader size={12} /> : <IconExternalLink size={12} />}
            </Group>
          </Anchor>
        </Row>
      );
    }

    // A value the card already holds, read-only. The scheduled intro call reads
    // the eligibility form's goal back rather than asking for it.
    case 'prefilledValue': {
      const current = asString(answers.values[widget.id]);
      return (
        <Row label={widget.label}>
          <Text size="sm" fw={500} ta="right" c={current ? undefined : 'dimmed'}>
            {current || '—'}
          </Text>
        </Row>
      );
    }

    // Card-specific kinds. The generic spine does not know what a weight-loss
    // estimate or a grid is; the card that owns the script renders them. Every
    // kind that expects `renderCardWidget` must be listed — the procedure-date
    // grid was not, so the hand-off's date chips fell through to `default` and
    // silently rendered nothing.
    case 'computed':
    case 'fact':
    case 'slotGrid':
    case 'procedureDateGrid':
    case 'locationSelect':
    case 'employerLocationSelect':
    case 'switchTrackLink':
    case 'employerSearch':
    case 'verificationTask':
    case 'insurerSelect':
    case 'insuranceEligibility':
    case 'profileField':
    case 'insuranceCardField':
    case 'policyholderField':
      return <>{renderCardWidget?.(widget) ?? null}</>;

    case 'group':
      // A block gated on the answers, e.g. "Who's your employer?" only for a
      // patient who has one. Its answers stay on the draft while it is hidden.
      if (widget.when && !widget.when(answers, context)) return null;
      // On v2.0 a group adds no box of its own: its rows stay on the section's
      // hairline list, under its title when it has one.
      if (columns) {
        return (
          <>
            {widget.title && (
              <Text size="xs" tt="uppercase" c="dimmed" pt={10} pb={4} style={{ letterSpacing: '0.04em' }}>
                {widget.title}
              </Text>
            )}
            {widget.items.map((item, index) => (
              <PatientAccessCallWidget key={index} {...props} widget={item} />
            ))}
          </>
        );
      }
      return (
        <Box mt={6} pt={2}>
          {widget.title && (
            <Text size="xs" tt="uppercase" c="dimmed" mb={2} style={{ letterSpacing: '0.04em' }}>
              {widget.title}
            </Text>
          )}
          <Stack gap={0}>
            {widget.items.map((item, index) => (
              <PatientAccessCallWidget key={index} {...props} widget={item} />
            ))}
          </Stack>
        </Box>
      );

    default:
      // Exhaustive on purpose: the procedure-date grid once fell through here
      // and rendered nothing.
      return renderUnhandled(widget);
  }
}
